import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MagazzinoDetailDialog from "@/components/MagazzinoDetailDialog";

const CAT_RANK = { modul: 0, pannell: 0, inverter: 1, accumul: 2, quadr: 3, monitor: 4, fissagg: 5, elettronic: 6, accessor: 7, altro: 8 };
const catRank = (cat) => {
  const low = (cat || "").toLowerCase();
  const fuori = low.includes("fuori listino");
  const base = low.replace("fuori listino", "").trim();
  for (const tok of Object.keys(CAT_RANK)) if (base.includes(tok)) return CAT_RANK[tok] * 2 + (fuori ? 1 : 0);
  return 999;
};
const fmtD = (d) => { if (!d) return "—"; const s = String(d).slice(0, 10); const p = s.split("-"); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : s; };

export function ArticoliGrouped({ articoli, countKey, countHeader, preventivi }) {
  const [open, setOpen] = useState({});
  const [movCod, setMovCod] = useState(null);
  const [commArt, setCommArt] = useState(null);
  const groups = useMemo(() => {
    const map = {};
    for (const a of articoli || []) {
      const cat = a.categoria || "Altro";
      (map[cat] = map[cat] || []).push(a);
    }
    return Object.entries(map)
      .map(([cat, items]) => ({ cat, items, mancante: items.reduce((s, x) => s + (x.mancante_tot || 0), 0) }))
      .sort((a, b) => catRank(a.cat) - catRank(b.cat) || a.cat.localeCompare(b.cat));
  }, [articoli]);
  const toggle = (cat) => setOpen((o) => ({ ...o, [cat]: !o[cat] }));

  return (
    <div className="space-y-3">
      {groups.map(({ cat, items, mancante }) => {
        const isOpen = open[cat] !== false;
        return (
          <div key={cat} className="overflow-hidden rounded-lg border border-border" data-testid={`mag-cat-${cat}`}>
            <button type="button" onClick={() => toggle(cat)} className="flex w-full items-center gap-2 bg-muted/40 px-4 py-2.5 text-left" data-testid={`mag-cat-toggle-${cat}`}>
              {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              <span className="font-heading text-sm font-bold tracking-tight">{cat}</span>
              <span className="rounded-full bg-background px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">{items.length} art.</span>
              <span className="ml-auto text-xs font-semibold text-red-600 dark:text-red-400">{mancante} pz mancanti</span>
            </button>
            {isOpen && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="py-2 pl-4 pr-3 font-medium">Codice</th>
                      <th className="py-2 pr-3 font-medium">Descrizione</th>
                      <th className="py-2 pr-3 text-right font-medium">Da comprare</th>
                      <th className="py-2 pr-3 text-right font-medium">In ritardo</th>
                      <th className="py-2 pr-3 text-right font-medium">Disp. ora</th>
                      <th className="py-2 pr-3 text-right font-medium">Prima copertura</th>
                      <th className="py-2 pr-4 text-right font-medium">{countHeader}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((a) => (
                      <tr key={a.codice} className="border-b border-border/50 last:border-0" data-testid={`mag-articolo-${a.codice}`}>
                        <td className="py-2 pl-4 pr-3 font-mono text-xs font-bold">{a.codice}</td>
                        <td className="py-2 pr-3">
                          <span className="inline-flex items-center gap-1.5">
                            {a.descrizione}
                            <button type="button" onClick={() => setMovCod(a.codice)} title="Vedi ingressi/uscite" className="text-muted-foreground transition-colors hover:text-primary" data-testid={`mag-info-${a.codice}`}><Info className="h-3.5 w-3.5" /></button>
                          </span>
                          {a.stato === "sconosciuto" && <span className="ml-2 rounded bg-red-400/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-red-600 dark:text-red-400">non a magazzino</span>}
                          {a.assegnato_a && <span className="ml-2 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400" title={`Coperto attingendo alla disponibilità di ${a.assegnato_a}`}>Assegnato a {a.assegnato_a} · coperto {a.coperto || 0}{a.mancante_tot > 0 ? ` · residuo ${a.mancante_tot}` : ""}</span>}
                        </td>
                        <td className="py-2 pr-3 text-right font-mono font-bold text-red-600 dark:text-red-400">{a.mancante_tot || 0}</td>
                        <td className="py-2 pr-3 text-right font-mono font-semibold text-amber-600 dark:text-amber-400">{a.in_ritardo ? a.in_ritardo : "—"}</td>
                        <td className="py-2 pr-3 text-right font-mono text-muted-foreground">{a.disponibile_ora == null ? "—" : a.disponibile_ora}</td>
                        <td className="py-2 pr-3 text-right font-mono text-xs">{a.data_copertura ? <span className="text-emerald-600 dark:text-emerald-400">{fmtD(a.data_copertura)}</span> : <span className="text-muted-foreground">nessun arrivo</span>}</td>
                        <td className="py-2 pr-4 text-right">
                          {a.commesse?.length ? (
                            <button type="button" onClick={() => setCommArt(a)} className="ml-auto font-mono underline decoration-dotted underline-offset-2 transition-colors hover:text-primary" data-testid={`mag-commesse-${a.codice}`} title="Vedi le fonti">{a[countKey]}</button>
                          ) : (
                            <span className="font-mono">{a[countKey]}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
      <MagazzinoDetailDialog open={!!movCod} onOpenChange={(o) => { if (!o) setMovCod(null); }} codice={movCod} preventivi={preventivi} />
      <Dialog open={!!commArt} onOpenChange={(o) => { if (!o) setCommArt(null); }}>
        <DialogContent className="max-w-lg" data-testid="commesse-modal">
          <DialogHeader><DialogTitle className="text-base">Commesse · <span className="font-mono">{commArt?.codice}</span></DialogTitle></DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto rounded-lg border border-border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted/60 text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Commessa</th>
                  <th className="px-3 py-2 text-left">Cliente</th>
                  <th className="px-3 py-2 text-left">Stato</th>
                  <th className="px-3 py-2 text-left">Data richiesta</th>
                  <th className="px-3 py-2 text-right">Q.tà mancante</th>
                </tr>
              </thead>
              <tbody>
                {(commArt?.commesse || []).map((c, i) => (
                  <tr key={i} className="border-t border-border/50" data-testid={`commessa-row-${i}`}>
                    <td className="px-3 py-1.5 font-mono">
                      {c.numero}
                    </td>
                    <td className="px-3 py-1.5">{c.cliente || "—"}</td>
                    <td className="px-3 py-1.5 text-muted-foreground">{c.stato || "—"}</td>
                    <td className="px-3 py-1.5 font-mono text-muted-foreground">{c.data_cons ? new Date(c.data_cons).toLocaleDateString("it-IT") : "—"}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-bold text-red-600 dark:text-red-400">{c.qta}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-muted-foreground">Commesse in corso che generano lo scoperto per questo articolo.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
